import { faFaceSmile } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Dropdown, DropdownContent, DropdownTrigger } from 'components/UI/V3/Dropdown';
import Picker, { IEmojiData, SKIN_TONE_LIGHT } from 'emoji-picker-react';
import React, { FunctionComponent, useCallback, useState } from 'react';

import styles from './EmojiPickerButton.module.scss';

interface EmojiPickerButtonProps {
    /** Called once with the selected emoji's UTF-8 string. The button closes
     *  the picker right after invoking this. */
    onSelect: (emoji: string) => void;
    /** Classes applied to the trigger `<button>` so callers can style the icon
     *  to fit its surrounding field (Subject input vs textarea corner, etc.). */
    className?: string;
    /** Accessible name on the trigger button — defaults to a generic label. */
    ariaLabel?: string;
    /** Picker anchor side relative to the trigger. Default 'bottom'. */
    side?: 'top' | 'bottom';
    /** Picker alignment along the anchor axis. Default 'end' so the picker's
     *  right edge lines up with the button — keeps it from overflowing left
     *  on narrow form columns. */
    align?: 'start' | 'center' | 'end';
}

/**
 * Trigger button + emoji picker popover, backed by V3 Dropdown (portal,
 * positioning, collision-detection, ClickAway dismissal, mobile bottom-sheet
 * fallback — all of which we'd otherwise have to wire by hand inside an
 * `overflow: auto` form column).
 *
 * Dropdown is controlled here so we can programmatically close on selection;
 * the uncontrolled API doesn't expose that signal.
 *
 * `emoji-picker-react@3` is configured to match the existing SMS Campaigns
 * usage: native OS emojis (lighter than sprite set), light skin tone default,
 * and the renamed "smileys_people" group surfaced as PEOPLE.
 */
const EmojiPickerButton: FunctionComponent<EmojiPickerButtonProps> = ({
    onSelect,
    className,
    ariaLabel = 'Insert emoji',
    side = 'bottom',
    align = 'end'
}) => {
    const [open, setOpen] = useState<boolean>(false);

    const handleEmojiClick = useCallback(
        (_event: React.MouseEvent, data: IEmojiData) => {
            onSelect(data.emoji);
            setOpen(false);
        },
        [onSelect]
    );

    return (
        <Dropdown
            open={open}
            onOpenChange={setOpen}
        >
            <DropdownTrigger>
                <button
                    type="button"
                    className={className}
                    aria-label={ariaLabel}
                >
                    <FontAwesomeIcon icon={faFaceSmile} />
                </button>
            </DropdownTrigger>
            <DropdownContent
                side={side}
                align={align}
                sideOffset={8}
                maxHeight={420}
                className={styles.popover}
            >
                <Picker
                    onEmojiClick={handleEmojiClick}
                    disableAutoFocus
                    skinTone={SKIN_TONE_LIGHT}
                    native
                    groupNames={{ smileys_people: 'PEOPLE' }}
                />
            </DropdownContent>
        </Dropdown>
    );
};

export default EmojiPickerButton;
